// Gate RUNTIME de la feature `menu-v2` (Fase A del framework de rollout).
//
// Disponibilidad capa 0 = build flag ENABLE_MENU_V2 (se mantiene) AND la resolución
// runtime per-comercio:
//   - build flag OFF  → SIEMPRE V1 (la build de prod ni siquiera tiene que decidir).
//   - build flag ON   → useFeatureVersion('menu-v2') elige V2 (treatment) o V1 (control).
//
// La build incluye AMBAS versiones como chunks lazy; el wrapper elige en render (no la
// constante build-time), por eso la decisión puede cambiar en runtime sin recargar bundle.
//
// DIFERIDO (capa 3, no en este PR #7): cuando la feature gradúe a GA (ga_forced=true) hay
// que comunicar al usuario "la versión anterior dejó de estar disponible" vía Announcements
// (plick-crud-announcement). Hoy la card simplemente se auto-oculta con ga_forced; el aviso
// proactivo de GA se implementa en un PR posterior.

import { lazy, Suspense, useCallback, type ComponentType } from 'react';
import LoadingPage from '@pages/LoadingPage';
import { ENABLE_MENU_V2 } from '@utils/featureFlags';
import { useFeatureVersion } from '@hooks/useFeatureVersion';
import MenuVersionInvitation from '@components/v4/featureRollout/MenuVersionInvitation';
import MenuVersionStatusChip from '@components/v4/featureRollout/MenuVersionStatusChip';
import FeatureErrorBoundary from '@components/v4/featureRollout/FeatureErrorBoundary';
import { MENU_FEATURE_KEY } from '@utils/featureRollout/constants';

// Pares V1 (control) / V2 (treatment) de cada pantalla del menú. Ambos lazy → la build
// los incluye como chunks separados, disponibles para que el runtime elija cualquiera.
const MenuV1 = lazy(() => import('@pages/Menu/Menu'));
const MenuV2 = lazy(() => import('@pages/Menu/MenuV2'));
const ProductsV1 = lazy(() => import('@pages/Menu/Products/Products'));
const ProductsV2 = lazy(() => import('@pages/Menu/Products/ProductsV2'));
const ExtrasV1 = lazy(() => import('@pages/Menu/Extras/Extras'));
const ExtrasV2 = lazy(() => import('@pages/Menu/Extras/ExtrasV2'));
const SectionsV1 = lazy(() => import('@pages/Menu/Sections/Sections'));
const SectionsV2 = lazy(() => import('@pages/Menu/Sections/SectionsV2'));

// Resuelve la variante una sola vez por subtree. Si el build flag está OFF no llamamos
// el hook (no hay decisión que tomar): la regla de hooks se respeta porque el flag es
// constante en una build dada → la rama es estable durante toda la vida del componente.
function pick<P extends object>(
  control: ComponentType<P>,
  treatment: ComponentType<P>,
  withInvitation = false,
): ComponentType<P> {
  if (!ENABLE_MENU_V2) return control;
  return function MenuV2Gate(props: P) {
    const { variant, experimentId, emitError } = useFeatureVersion(MENU_FEATURE_KEY);
    const Control = control;
    const Treatment = treatment;
    const isTreatment = variant === 'treatment';

    // Telemetría de error de la variante treatment (fire-and-forget). El hook ya valida que solo
    // emita en treatment y adjunta experiment_id + payload no-PII. Memoizado para no re-armar el
    // boundary en cada render.
    const handleError = useCallback((error: unknown) => emitError(error, 'menu_v2_gate'), [emitError]);

    return (
      <Suspense fallback={<LoadingPage />}>
        {/* La invitación/regreso solo se monta en el shell del menú (withInvitation). El CHIP
            (canal PULL) también se monta SIEMPRE aquí — tanto en control como en treatment —
            y se auto-gobierna por elegibilidad/variant + manual_entry (no por invite_surface). */}
        {withInvitation && (
          <>
            <MenuVersionStatusChip />
            <MenuVersionInvitation />
          </>
        )}
        {isTreatment ? (
          // La variante treatment (V2) va envuelta en el error boundary: si su render/runtime
          // falla, (1) se emite behavior='error' ligado al experimento (guardrail error_rate) y
          // (2) caemos a V1/control en vez de romper la app. resetKey=experimentId re-arma el
          // boundary si el comercio/experimento cambia.
          <FeatureErrorBoundary fallback={<Control {...props} />} onError={handleError} resetKey={experimentId ?? ''}>
            <Treatment {...props} />
          </FeatureErrorBoundary>
        ) : (
          <Control {...props} />
        )}
      </Suspense>
    );
  };
}

// Menu = shell del menú → lleva la card de invitación/regreso encima. Las sub-pantallas
// (Products/Extras/Sections) solo intercambian variante, sin re-renderizar la card.
export const Menu = pick(MenuV1, MenuV2, true);
export const Products = pick(ProductsV1, ProductsV2);
export const Extras = pick(ExtrasV1, ExtrasV2);
export const Sections = pick(SectionsV1, SectionsV2);

export { MENU_FEATURE_KEY };
